import * as Sentry from '@sentry/react';

export const initSentry = (): boolean => {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  const environment = process.env.REACT_APP_SENTRY_ENVIRONMENT || 'development';
  const release = process.env.REACT_APP_SENTRY_RELEASE || '1.0.0';

  if (!dsn) {
    return false;
  }

  // Prevent multiple initialization
  if (Sentry.isInitialized()) {
    return true;
  }

  try {
    Sentry.init({
      dsn,
      environment,
      release,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],
      tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      beforeSend(event) {
        // Filter out non-error events in development, but allow user feedback and bug reports
        if (environment === 'development' && 
            event.level !== 'error' && 
            event.type !== 'feedback' &&
            !(typeof event.tags?.source === 'string' && event.tags.source.includes('custom_dialog'))) {
          return null;
        }
        return event;
      },
    });

    return true;
  } catch (error) {
    console.error('Failed to initialize Sentry:', error);
    return false;
  }
};

// Using Sentry.captureFeedback API directly - no UI integration needed

export const captureFeedback = (feedbackData: {
  name?: string;
  email?: string;
  message: string;
  attachments?: Array<{ filename: string; data: Uint8Array; contentType: string }>;
  tags?: Record<string, string>;
}): string => {
  // Check if Sentry is initialized
  if (!Sentry.isInitialized()) {
    console.error('Sentry is not initialized. Cannot capture feedback.');
    return '';
  }
  
  const scope = Sentry.getCurrentScope();
  const user = scope?.getUser();
  
  try {
    // Prepare feedback data
    const feedback = {
      name: feedbackData.name || user?.username || user?.name || user?.email || 'Anonymous',
      email: feedbackData.email || user?.email || '',
      message: feedbackData.message,
    };
    
    // Prepare hint with additional context
    const hint: any = {
      captureContext: {
        tags: {
          source: 'custom_dialog',
          issue_type: feedbackData.tags?.issue_type || 'bug',
          has_screenshot: feedbackData.attachments ? 'true' : 'false',
          ...feedbackData.tags,
        },
        user: {
          id: user?.id,
          username: user?.username,
          email: user?.email,
        },
      },
    };
    
    // Add attachments if provided
    if (feedbackData.attachments && feedbackData.attachments.length > 0) {
      hint.attachments = feedbackData.attachments.map(attachment => ({
        filename: attachment.filename,
        data: attachment.data,
        contentType: attachment.contentType,
      }));
    }
    
    // Capture the feedback and return the event ID
    const eventId = Sentry.captureFeedback(feedback, hint);
    return eventId;
  } catch (error) {
    console.error('Error capturing feedback:', error);
    return '';
  }
};

// Legacy function for backward compatibility
export const showUserFeedback = (options: Record<string, any> = {}): void => {
  // For now, capture a basic feedback message
  captureFeedback({
    message: 'User requested feedback dialog - please implement custom UI',
    tags: { source: 'legacy_button' },
    ...options,
  });
};
